// ============================================================================
// API Service — centralized HTTP client for PDA Backend
// Base URL: /api (proxied by Nginx to Go backend on port 8090)
// ============================================================================

const API_BASE = '/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE;
  }

  // ─── Token Management ──────────────────────────────────────────────────

  getAccessToken() {
    return localStorage.getItem('pda_access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('pda_refresh_token');
  }

  setTokens(accessToken, refreshToken) {
    localStorage.setItem('pda_access_token', accessToken);
    localStorage.setItem('pda_refresh_token', refreshToken);
  }

  clearTokens() {
    localStorage.removeItem('pda_access_token');
    localStorage.removeItem('pda_refresh_token');
    localStorage.removeItem('pda_user');
  }

  getStoredUser() {
    try {
      const data = localStorage.getItem('pda_user');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  setStoredUser(user) {
    localStorage.setItem('pda_user', JSON.stringify(user));
  }

  // ─── HTTP Methods ──────────────────────────────────────────────────────

  async request(method, path, body = null, requireAuth = true) {
    const headers = { 'Content-Type': 'application/json' };

    if (requireAuth) {
      const token = this.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const options = { method, headers };
    if (body) {
      options.body = JSON.stringify(body);
    }

    let response = await fetch(`${this.baseURL}${path}`, options);

    // If 401 and we have a refresh token, try to refresh
    if (response.status === 401 && requireAuth) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.getAccessToken()}`;
        options.headers = headers;
        response = await fetch(`${this.baseURL}${path}`, options);
      }
    }

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.message || 'Request failed');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        return false;
      }

      const data = await response.json();
      if (data.success && data.data) {
        this.setTokens(data.data.access_token, data.data.refresh_token);
        return true;
      }
      return false;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  get(path) {
    return this.request('GET', path);
  }

  post(path, body) {
    return this.request('POST', path, body);
  }

  put(path, body) {
    return this.request('PUT', path, body);
  }

  patch(path, body) {
    return this.request('PATCH', path, body);
  }

  delete(path) {
    return this.request('DELETE', path);
  }

  // ─── Auth API ──────────────────────────────────────────────────────────

  async login(email, password) {
    const data = await this.request('POST', '/v1/auth/login', { email, password }, false);
    if (data.success && data.data) {
      this.setTokens(data.data.access_token, data.data.refresh_token);
      this.setStoredUser(data.data.user);
      return data.data;
    }
    throw new Error('Login failed');
  }

  async logout() {
    try {
      await this.post('/v1/auth/logout');
    } catch {
      // Ignore errors on logout
    } finally {
      this.clearTokens();
    }
  }

  async getMe() {
    const data = await this.get('/v1/auth/me');
    if (data.success && data.data) {
      this.setStoredUser(data.data);
      return data.data;
    }
    throw new Error('Failed to get user');
  }

  async changePassword(currentPassword, newPassword) {
    return this.put('/v1/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }
}

// Singleton
const api = new ApiService();
export default api;
